import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { CheckCircle, CreditCard, Loader2 } from 'lucide-react';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

const BillingPage = () => {
  const { user, token, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  const plans = [
    {
      id: 'basic',
      name: 'Basic',
      price: '$4.99',
      period: '/month',
      features: ['10 renewals', 'Email notifications', 'Full dashboard', 'Priority reminders'],
      popular: false
    },
    {
      id: 'pro',
      name: 'Pro',
      price: '$9.99',
      period: '/month',
      features: ['50 renewals', 'Email & SMS notifications', 'Calendar sync', 'Custom reminder schedules'],
      popular: true
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: '$19.99',
      period: '/month',
      features: ['Unlimited renewals', 'All notification channels', 'API access', 'Priority support'],
      popular: false
    }
  ];

  // Check payment status on return from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams]);

  // Fetch payment history
  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API}/billing/history`, {
          headers,
          withCredentials: true
        });
        setPaymentHistory(response.data);
      } catch (error) {
        console.error('Error fetching payment history:', error);
      } finally {
        setHistoryLoading(false);
      }
    };
    fetchHistory();
  }, [token]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment status check timed out. Please refresh the page.');
      setCheckingPayment(false);
      return;
    }

    setCheckingPayment(true);

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/billing/status/${sessionId}`, {
        headers,
        withCredentials: true
      });

      if (response.data.payment_status === 'paid') {
        toast.success('Payment successful! Your plan has been upgraded.');
        await refreshUser();
        setCheckingPayment(false);
        // Clean URL
        window.history.replaceState({}, document.title, '/billing');
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        setCheckingPayment(false);
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  };

  const handleSubscribe = async (planId) => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/billing/checkout`, {
        plan: planId,
        origin_url: window.location.origin
      }, {
        headers,
        withCredentials: true
      });

      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setLoading(false);
    }
  };

  const isCurrentPlan = (planId) => {
    return user?.subscription_plan === planId;
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="billing-page">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-ink font-display">Billing</h1>
          <p className="text-ink-600 mt-1">Manage your subscription and view payment history</p>
        </div>

        {/* Payment Status Checking */}
        {checkingPayment && (
          <Card className="bg-cream border-ink-200 mb-6">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Loader2 className="w-5 h-5 animate-spin text-respondent" />
                <p className="text-ink font-medium">Confirming your payment...</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Current Plan */}
        <Card className="bg-white border border-ink-200 shadow-sm mb-8">
          <CardHeader>
            <CardTitle className="font-display">Current Plan</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-xl font-bold text-ink capitalize">
                    {user?.subscription_plan || 'Free'}
                  </p>
                  {user?.subscription_plan !== 'free' && (
                    <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>
                  )}
                </div>
                <p className="text-ink-600 mt-1">
                  {user?.subscription_plan === 'free' 
                    ? 'Limited to 3 renewals. Upgrade for more.' 
                    : user?.subscription_plan === 'basic'
                    ? 'Up to 10 renewals with email notifications.'
                    : user?.subscription_plan === 'pro'
                    ? 'Up to 50 renewals with email & SMS notifications.'
                    : 'Unlimited renewals with all features.'
                  }
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Plans Grid */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-ink font-display mb-4">
            {user?.subscription_plan === 'free' ? 'Upgrade Your Plan' : 'Available Plans'}
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {plans.map((plan) => (
              <Card 
                key={plan.id}
                className={`relative bg-white border shadow-sm ${
                  plan.popular ? 'border-respondent ring-2 ring-ink/20' : 'border-ink-200'
                } ${isCurrentPlan(plan.id) ? 'bg-paper' : ''}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-respondent text-white text-xs font-semibold px-3 py-1 rounded-full">
                    Most Popular
                  </div>
                )}
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-ink font-display">{plan.name}</h3>
                  <div className="mt-4 mb-6">
                    <span className="text-4xl font-bold text-ink">{plan.price}</span>
                    <span className="text-ink-600">{plan.period}</span>
                  </div>
                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-ink-600">
                        <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  {isCurrentPlan(plan.id) ? (
                    <Button disabled className="w-full bg-cream text-ink-600">
                      Current Plan
                    </Button>
                  ) : (
                    <Button 
                      className={`w-full ${
                        plan.popular 
                          ? 'bg-ink hover:bg-black text-white' 
                          : 'bg-cream hover:bg-cream text-ink'
                      }`}
                      onClick={() => handleSubscribe(plan.id)}
                      disabled={loading}
                      data-testid={`subscribe-${plan.id}-btn`}
                    >
                      {loading ? 'Processing...' : 'Subscribe'}
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Payment History */}
        <Card className="bg-white border border-ink-200 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-display">
              <CreditCard className="w-5 h-5" />
              Payment History
            </CardTitle>
            <CardDescription>Your recent transactions</CardDescription>
          </CardHeader>
          <CardContent>
            {historyLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-ink-600/70" />
              </div>
            ) : paymentHistory.length === 0 ? (
              <p className="text-center py-8 text-ink-600">No payment history yet</p>
            ) : (
              <div className="space-y-3">
                {paymentHistory.map((txn) => (
                  <div 
                    key={txn.transaction_id}
                    className="flex items-center justify-between py-3 border-b border-ink-200 last:border-0"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-md flex items-center justify-center ${
                        txn.payment_status === 'paid' ? 'bg-emerald-50' : 'bg-amber-50'
                      }`}>
                        <CreditCard className={`w-5 h-5 ${
                          txn.payment_status === 'paid' ? 'text-emerald-600' : 'text-amber-600'
                        }`} />
                      </div>
                      <div>
                        <p className="font-medium text-ink capitalize">{txn.plan} Plan</p>
                        <p className="text-sm text-ink-600">
                          {format(parseISO(txn.created_at), 'MMM d, yyyy')}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-ink">${txn.amount.toFixed(2)}</p>
                      <Badge className={
                        txn.payment_status === 'paid' 
                          ? 'bg-emerald-100 text-emerald-700' 
                          : 'bg-amber-100 text-amber-700'
                      }>
                        {txn.payment_status === 'paid' ? 'Paid' : 'Pending'}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default BillingPage;
